import { useState, useEffect, useRef, useCallback } from 'react'
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import Button from './ui/Button'

// Set up PDF worker with optimizations
if (typeof window !== 'undefined') {
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
    pdfjs.GlobalWorkerOptions.useSystemFonts = true
}

export default function PresentationViewer({ pdfUrl, title }) {
    const [numPages, setNumPages] = useState(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [loading, setLoading] = useState(true)
    const [progress, setProgress] = useState(0)
    const [error, setError] = useState(null)
    const [scale, setScale] = useState(1)
    const [containerWidth, setContainerWidth] = useState(0)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showControls, setShowControls] = useState(true)
    const [fullscreenWidth, setFullscreenWidth] = useState(window.innerWidth)
    const [fullscreenHeight, setFullscreenHeight] = useState(window.innerHeight)
    const [isPowerPointAspectRatio, setIsPowerPointAspectRatio] = useState(true)
    const [pdfAspectRatio, setPdfAspectRatio] = useState(null)
    const containerRef = useRef(null)
    const fullscreenRef = useRef(null)
    const controlHideTimeoutRef = useRef(null)
    const startTimeRef = useRef(Date.now())

    // Helper functions defined before useEffects
    const exitFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen()
            } else if (document.webkitFullscreenElement) {
                await document.webkitExitFullscreen()
            }
            setIsFullscreen(false)
            setShowControls(true)
        } catch (err) {
            console.error('Failed to exit fullscreen:', err)
        }
    }, [])

    const calculateOptimalDimensions = useCallback(() => {
        const controlsHeight = 80
        const availableHeight = window.innerHeight - controlsHeight
        const availableWidth = window.innerWidth - 20 // 10px padding on each side
        
        console.log(`📐 Screen resolution: ${window.innerWidth}x${window.innerHeight}`)
        console.log(`Available space (after controls/padding): ${availableWidth.toFixed(0)}x${availableHeight.toFixed(0)}`)
        console.log(`Detected PDF aspect ratio: ${pdfAspectRatio?.toFixed(3) || 'unknown'}`)
        
        let optimalWidth = availableWidth
        let optimalHeight = availableHeight
        
        // If we have a valid PDF aspect ratio, use it to calculate optimal fit
        if (pdfAspectRatio && pdfAspectRatio > 0.2 && pdfAspectRatio < 5) {
            const heightBasedWidth = availableHeight * pdfAspectRatio
            const widthBasedHeight = availableWidth / pdfAspectRatio
            
            console.log(`Height-based width: ${heightBasedWidth.toFixed(0)}px, Width-based height: ${widthBasedHeight.toFixed(0)}px`)
            
            // Check if height-limited (document fits within width when stretched to full height)
            if (heightBasedWidth > 200 && heightBasedWidth <= availableWidth) {
                optimalWidth = heightBasedWidth
                optimalHeight = availableHeight
                console.log(`✓ Height-limited: Using full height, width: ${optimalWidth.toFixed(0)}x${optimalHeight.toFixed(0)}`)
            } 
            // Check if width-limited (document fits within height when stretched to full width)
            else if (widthBasedHeight > 200 && widthBasedHeight <= availableHeight) {
                optimalWidth = availableWidth
                optimalHeight = widthBasedHeight
                console.log(`✓ Width-limited: Using full width, dimensions: ${optimalWidth.toFixed(0)}x${optimalHeight.toFixed(0)}`)
            } 
            // Fallback: use all available space
            else {
                optimalWidth = availableWidth
                optimalHeight = availableHeight
                console.log(`⚠ Fallback: Using full available space: ${optimalWidth.toFixed(0)}x${optimalHeight.toFixed(0)}`)
            }
        } else {
            console.warn(`⚠ Invalid or missing aspect ratio (${pdfAspectRatio}), using full space`)
            optimalWidth = availableWidth
            optimalHeight = availableHeight
        }
        
        setFullscreenWidth(optimalWidth)
        setFullscreenHeight(optimalHeight)
        
        return { width: optimalWidth, height: optimalHeight }
    }, [pdfAspectRatio])

    const enterFullscreen = useCallback(async () => {
        // First set fullscreen state and show controls
        setIsFullscreen(true)
        setShowControls(true)
        
        // Request fullscreen on the element after render
        setTimeout(async () => {
            const element = fullscreenRef.current || document.querySelector('[data-fullscreen-container]')
            if (element) {
                try {
                    if (element.requestFullscreen) {
                        await element.requestFullscreen()
                    } else if (element.webkitRequestFullscreen) {
                        await element.webkitRequestFullscreen()
                    }
                } catch (err) {
                    console.error('Failed to enter fullscreen:', err)
                }
            }
            
            // Calculate optimal dimensions after a brief delay to ensure screen is ready
            setTimeout(() => {
                calculateOptimalDimensions()
            }, 100)
        }, 0)
    }, [calculateOptimalDimensions])

    const goToPrevPage = useCallback(() => {
        setPageNumber((prev) => Math.max(1, prev - 1))
    }, [])

    const goToNextPage = useCallback(() => {
        setPageNumber((prev) => (numPages ? Math.min(prev + 1, numPages) : prev))
    }, [numPages])

    // Handle responsive sizing
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth
                setContainerWidth(width)
                
                // Calculate scale based on container width
                if (width < 500) {
                    setScale(0.75)
                } else if (width < 768) {
                    setScale(0.9)
                } else if (width < 1024) {
                    setScale(1)
                } else {
                    setScale(1.1)
                }
            }
        }

        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // ESC: Exit fullscreen (only in fullscreen)
            if (e.key === 'Escape' && isFullscreen) {
                e.preventDefault()
                exitFullscreen()
                return
            }
            
            // F key: Enter fullscreen (only in normal mode)
            if ((e.key === 'f' || e.key === 'F') && !isFullscreen && numPages) {
                e.preventDefault()
                enterFullscreen()
                return
            }
            
            // Arrow keys: Navigate pages (works in both modes)
            if (e.key === 'ArrowLeft' && numPages) {
                e.preventDefault()
                setPageNumber((prev) => Math.max(1, prev - 1))
            }
            if (e.key === 'ArrowRight' && numPages) {
                e.preventDefault()
                setPageNumber((prev) => Math.min(prev + 1, numPages))
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isFullscreen, numPages, enterFullscreen, exitFullscreen])

    // Auto-hide controls in fullscreen
    useEffect(() => {
        if (isFullscreen) {
            const handleMouseMove = () => {
                setShowControls(true)
                clearTimeout(controlHideTimeoutRef.current)
                controlHideTimeoutRef.current = setTimeout(() => {
                    setShowControls(false)
                }, 3000)
            }

            const handleResize = () => {
                console.log(`🔄 Resize event during fullscreen`)
                calculateOptimalDimensions()
            }

            handleResize() // Set initial dimensions when entering fullscreen
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('resize', handleResize)
            return () => {
                window.removeEventListener('mousemove', handleMouseMove)
                window.removeEventListener('resize', handleResize)
                clearTimeout(controlHideTimeoutRef.current)
            }
        }
    }, [isFullscreen, calculateOptimalDimensions])

    useEffect(() => {
        setLoading(true)
        setError(null)
        setProgress(0)
        startTimeRef.current = Date.now()
    }, [pdfUrl])

    const onDocumentLoadSuccess = ({ numPages }) => {
        const loadTime = Date.now() - startTimeRef.current
        console.log(`PDF loaded successfully in ${loadTime}ms`)
        setNumPages(numPages)
        setLoading(false)
        setProgress(100)
    }

    const onDocumentLoadError = (error) => {
        console.error('Failed to load PDF:', error)
        setError('Failed to load presentation. Please try downloading the file instead.')
        setLoading(false)
    }

    const onLoadProgress = (data) => {
        if (data.total > 0) {
            const percentage = Math.round((data.loaded / data.total) * 100)
            setProgress(Math.min(percentage, 99))
        }
    }

    // Detect PDF aspect ratio after first page renders
    const detectAspectRatio = (pageElement) => {
        if (!pageElement) return
        
        const canvas = pageElement.querySelector('canvas')
        if (!canvas) return
        
        // Get dimensions - retry a few times if canvas hasn't rendered yet
        let attempts = 0
        const checkDimensions = () => {
            const width = canvas.offsetWidth
            const height = canvas.offsetHeight
            
            if (width && height) {
                const aspectRatio = width / height
                
                // Validate aspect ratio is reasonable (between 0.4 and 3)
                if (aspectRatio > 0.4 && aspectRatio < 3) {
                    setPdfAspectRatio(aspectRatio)
                    
                    // PowerPoint ratios: 16:9 ≈ 1.778, 4:3 ≈ 1.333
                    // Standard PDF (letter): 8.5x11 ≈ 0.727
                    const is16to9 = Math.abs(aspectRatio - 1.778) < 0.15
                    const is4to3 = Math.abs(aspectRatio - 1.333) < 0.15
                    const isPowerPoint = is16to9 || is4to3
                    
                    console.log(`✓ Detected aspect ratio: ${aspectRatio.toFixed(3)} (${width.toFixed(0)}x${height.toFixed(0)}), is PowerPoint: ${isPowerPoint}`)
                    setIsPowerPointAspectRatio(isPowerPoint)
                } else {
                    console.warn(`Invalid aspect ratio: ${aspectRatio.toFixed(3)}, expected between 0.4-3`)
                    if (attempts < 3) {
                        attempts++
                        setTimeout(checkDimensions, 100)
                    }
                }
            } else if (attempts < 3) {
                // Canvas hasn't rendered yet, retry
                attempts++
                setTimeout(checkDimensions, 100)
            }
        }
        
        checkDimensions()
    }

    // Keyboard shortcuts

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                setIsFullscreen(false)
                setShowControls(true)
            }
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        }
    }, [])

    if (error) {
        return (
            <div className="flex items-center justify-center py-32 border-2 border-red-600 bg-white dark:bg-zinc-950">
                <div className="text-center">
                    <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
                    <Button as="a" href={pdfUrl} download className="mt-4">
                        Download PDF
                    </Button>
                </div>
            </div>
        )
    }

    const isFirstLoad = loading && pageNumber === 1

    if (isFullscreen) {
        return (
            <div 
                ref={fullscreenRef}
                data-fullscreen-container
                className="w-screen h-screen bg-black flex flex-col justify-center items-center relative overflow-hidden"
            >
                {/* Fullscreen Mode - Responsive PDF Display */}
                <div className={`w-full flex-1 flex justify-center items-center ${isPowerPointAspectRatio ? 'overflow-hidden' : 'overflow-auto'}`}>
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        onLoadProgress={onLoadProgress}
                        loading={null}
                    >
                        <div 
                            ref={detectAspectRatio}
                            style={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                width: '100%',
                                height: '100%'
                            }}
                        >
                            <Page 
                                pageNumber={pageNumber} 
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                                height={isPowerPointAspectRatio ? fullscreenHeight : undefined}
                                width={fullscreenWidth}
                            />
                        </div>
                    </Document>
                </div>

                {/* Fullscreen Controls - Auto hide after 3s */}
                <div 
                    className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent transition-opacity duration-300 ${
                        showControls ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ pointerEvents: showControls ? 'auto' : 'none' }}
                >
                    <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3 mx-auto px-2 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                                className="px-2 sm:px-3 py-1.5 rounded-none border border-white/40 bg-white/10 hover:bg-accent hover:text-black hover:border-accent text-white disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                                title="Previous page (← key)"
                            >
                                ← Prev
                            </button>
                            <div className="text-white text-xs sm:text-sm font-medium px-2 sm:px-3 py-1.5 bg-white/10 border border-white/40 whitespace-nowrap font-mono">
                                {pageNumber} / {numPages}
                            </div>
                            <button
                                onClick={goToNextPage}
                                disabled={pageNumber >= numPages}
                                className="px-2 sm:px-3 py-1.5 rounded-none border border-white/40 bg-white/10 hover:bg-accent hover:text-black hover:border-accent text-white disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                                title="Next page (→ key)"
                            >
                                Next →
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={exitFullscreen}
                                className="px-2 sm:px-3 py-1.5 rounded-none border border-white/40 bg-white/10 hover:bg-accent hover:text-black hover:border-accent text-white transition text-xs sm:text-sm font-medium"
                                title="Exit fullscreen (ESC)"
                            >
                                Exit (ESC)
                            </button>
                        </div>
                    </div>
                </div>

                {isFirstLoad && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                        <div className="flex flex-col items-center gap-2 bg-white px-6 py-4 border-2 border-black">
                            <div className="w-6 h-6 border-3 border-zinc-300 border-t-accent rounded-full animate-spin" />
                            <p className="text-xs text-zinc-600">Loading presentation.</p>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="space-y-4 w-full">
            {/* Controls - Responsive */}
            <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap bg-white dark:bg-zinc-950 border-2 border-black dark:border-white p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <Button variant="secondary" onClick={goToPrevPage} disabled={pageNumber <= 1} className="text-xs sm:text-sm" title="Previous page (← key)">
                        Previous
                    </Button>
                    <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-white dark:bg-zinc-950 border-2 border-black dark:border-white text-xs sm:text-sm text-black dark:text-white font-mono">
                        <span className="hidden sm:inline">Page</span>
                        <input
                            type="number"
                            value={pageNumber}
                            onChange={(e) => {
                                const val = parseInt(e.target.value)
                                if (val >= 1 && val <= numPages) {
                                    setPageNumber(val)
                                }
                            }}
                            className="w-8 sm:w-10 text-center border-0 bg-transparent font-semibold focus:outline-none"
                        />
                        <span>of {numPages || '?'}</span>
                    </div>
                    <Button variant="secondary" onClick={goToNextPage} disabled={pageNumber >= numPages} className="text-xs sm:text-sm" title="Next page (→ key)">
                        Next
                    </Button>
                </div>

                <div className="flex items-center gap-2">
                    <Button onClick={enterFullscreen} className="text-xs sm:text-sm" title="Enter fullscreen (F)">
                        Fullscreen
                    </Button>
                    <Button as="a" href={pdfUrl} download variant="secondary" className="text-xs sm:text-sm">
                        Download
                    </Button>
                </div>
            </div>

            {/* Progress Bar - Only on first load */}
            {isFirstLoad && progress < 100 && (
                <div className="bg-white dark:bg-zinc-950 border-2 border-black dark:border-white p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">Initializing presentation.</span>
                        <span className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">{progress}%</span>
                    </div>
                    <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-2 overflow-hidden">
                        <div
                            className="bg-accent h-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            )}

            {/* PDF Viewer - Responsive Container */}
            <div
                ref={containerRef}
                className="w-full flex justify-center bg-zinc-100 dark:bg-zinc-950 border-2 border-black dark:border-white overflow-auto relative"
                style={{ minHeight: '400px' }}
            >
                <div className="flex justify-center items-start p-2 sm:p-4">
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        onLoadProgress={onLoadProgress}
                        loading={null}
                        noData={
                            <div className="flex items-center justify-center w-full h-full">
                                <p className="text-zinc-600 dark:text-zinc-400">No PDF available.</p>
                            </div>
                        }
                    >
                        <div 
                            ref={detectAspectRatio}
                            style={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                maxWidth: '100%'
                            }}
                        >
                            <Page 
                                pageNumber={pageNumber} 
                                scale={scale}
                                renderTextLayer={true}
                                renderAnnotationLayer={true}
                                width={containerWidth > 0 ? Math.min(containerWidth - 32, 900) : undefined}
                            />
                        </div>
                    </Document>
                </div>
                {isFirstLoad && (
                    <div className="absolute inset-0 bg-white/30 dark:bg-zinc-950/30 flex items-center justify-center z-10">
                        <div className="flex flex-col items-center gap-2 bg-white dark:bg-zinc-950 px-4 sm:px-6 py-3 sm:py-4 border-2 border-black dark:border-white">
                            <div className="w-6 h-6 border-3 border-zinc-300 dark:border-zinc-700 border-t-accent rounded-full animate-spin" />
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">Loading PDF engine.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
